import React, { useEffect, useState, useRef } from 'react';
// 🔹 ADDED useLocation to this import line
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore'; 
import { db, analytics } from './firebase';
import { logEvent } from 'firebase/analytics';

// Pages
import Profile from './components/Profile';
import StudentDashboard from './components/StudentDashboard';
import TeacherDashboard from './components/teacher/TeacherDashboard';
import StudentDetails from './components/teacher/StudentDetails';
import AdminDashboard from './components/admin/AdminDashboard';
import UsersManagement from './components/admin/UsersManagement';
import AdminAnalytics from './components/admin/AdminAnalytics';
import AdminStudentDashboard from './components/admin/AdminStudentDashboard';
import SignIn from './SignIn';
import SignUp from './SignUp';
import Welcome from './Welcome';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SubscribePage from './components/SubscribePage';
import ThankYouPage from './pages/ThankYouPage';
import LessonVocab from './components/lessons/LessonVocab';
import LessonPractice from './components/lessons/LessonPractice';
import LessonReading from './components/lessons/LessonReading';
import LessonWriting from './components/lessons/LessonWriting';
import LessonMock from './routes/LessonMockWrapper';
import InstallButton from './components/InstallButton';

// ✅ This handles the tracking correctly
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
    console.log("📈 Analytics: Page view logged for", location.pathname);
  }, [location]);

  return null; 
}

function RedirectHandler({ role }) {
  const navigate = useNavigate();
  const redirected = useRef(false);

  useEffect(() => {
    if (!role || redirected.current) return;
    redirected.current = true;

    if (role === 'teacher') {
      navigate('/teacher', { replace: true });
    } else if (role === 'admin') {
      navigate('/admin', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [role, navigate]);

  return <div>Redirecting...</div>;
}

function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setUserRole(null);
        setLoading(false);
        return;
      }

      setUser(currentUser);

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        
        // Update activity
        await updateDoc(userRef, {
          lastLoginAt: serverTimestamp()
        }).catch(err => console.warn("Timestamp update skipped"));

        // Fetch role
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const role = userDoc.data().role || 'student';
          setUserRole(role);
        } else {
          setUserRole('incomplete');
        }
      } catch (error) {
        console.error('❌ Error in Auth State Change:', error);
        setUserRole(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Router>
        <AnalyticsTracker />
        <Routes>
          <Route
            path="/"
            element={
              !user
                ? <Welcome />
                : userRole
                  ? <RedirectHandler role={userRole} />
                  : <div>Loading role...</div>
            }
          />

          <Route path="/signin" element={!user ? <SignIn /> : <Navigate to="/" replace />} />
          <Route path="/signup" element={!user ? <SignUp /> : <Navigate to="/" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />

          {/* Student */}
          {(userRole === 'student' || userRole === 'incomplete') && (
            <>
              <Route path="/dashboard" element={<StudentDashboard />} />
              <Route path="/lesson/:lessonId/vocab" element={<LessonVocab />} />
              <Route path="/lesson/:lessonId/practice" element={<LessonPractice />} />
              <Route path="/lesson/:lessonId/reading" element={<LessonReading />} />
              <Route path="/lesson/:lessonId/writing" element={<LessonWriting />} />
              <Route path="/lessonMock/:mockId/*" element={<LessonMock />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/subscribe" element={<SubscribePage />} />
            </>
          )}

          {/* Teacher */}
          {userRole === 'teacher' && (
            <>
              <Route path="/teacher" element={<TeacherDashboard />} />
              <Route path="/teacher/student/:studentId/:mockId" element={<StudentDetails />} />
              <Route path="/profile" element={<Profile />} />
            </>
          )}

          {/* Admin */}
          {userRole === 'admin' && (
            <>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<UsersManagement />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/student/:studentId" element={<AdminStudentDashboard />} />
            </>
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>

      <InstallButton />
    </>
  );
}

export default App;